import React, { useEffect, useState } from "react";
import client from "@/lib/api";
import AdminDashboard from "@/pages/AdminDashboard";
import { SupervisorLayout } from "@/layouts/SupervisorLayout";
import { ClipboardList, Clock, Radio, CheckCircle2 } from "lucide-react";

/**
 * Supervisor → Build Orders.
 * Renders the four real summary cards on top and mounts the existing
 * AdminDashboard as the content (preserving the table, + New Build Order
 * modal, WhatsappInput stability fix, bulk-delete, WhatsApp state,
 * customer-link actions and every existing lifecycle behavior).
 */
function SummaryCard({ icon: Icon, label, value, accent, testid }) {
  return (
    <div className="rounded-xl border p-5 flex items-start gap-4 min-w-0" style={{ borderColor: "rgba(255,255,255,0.08)", background: "#0d0d10" }} data-testid={testid}>
      <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{ background: accent.bg, color: accent.fg }}><Icon size={18}/></div>
      <div className="min-w-0 flex-1">
        <div className="text-[10px] tracking-[0.28em] uppercase text-white/45 font-semibold">{label}</div>
        <div className="mt-1 text-3xl font-semibold text-white tabular-nums" data-testid={`${testid}-value`}>{value}</div>
      </div>
    </div>
  );
}

function SummaryCards() {
  const [c, setC] = useState({ total: "—", waiting: "—", live: "—", completed: "—" });
  const load = async () => {
    try {
      const { data } = await client.get("/orders");
      const rows = Array.isArray(data) ? data : (data.orders || []);
      const by = (s) => rows.filter(o => o.status === s).length;
      setC({ total: rows.length, waiting: by("WAITING"), live: by("LIVE") + by("PAUSED"), completed: by("COMPLETED") });
    } catch { /* silent — layout still renders */ }
  };
  useEffect(() => { load(); const id = setInterval(load, 6000); return () => clearInterval(id); }, []);
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-testid="summary-cards">
      <SummaryCard icon={ClipboardList} label="Total Orders" value={c.total} accent={{ bg: "rgba(0,229,255,0.10)", fg: "var(--accent)" }} testid="card-total" />
      <SummaryCard icon={Clock}         label="Waiting"      value={c.waiting} accent={{ bg: "rgba(255,255,255,0.06)", fg: "rgba(255,255,255,0.75)" }} testid="card-waiting" />
      <SummaryCard icon={Radio}         label="Live"         value={c.live} accent={{ bg: "rgba(255,51,51,0.10)", fg: "#ff5c5c" }} testid="card-live" />
      <SummaryCard icon={CheckCircle2}  label="Completed"    value={c.completed} accent={{ bg: "rgba(0,255,102,0.08)", fg: "var(--success)" }} testid="card-completed" />
    </div>
  );
}

export default function BuildOrdersPage() {
  return (
    <SupervisorLayout>
      <div className="p-5 md:p-8 max-w-[1600px]">
        <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl md:text-3xl font-semibold text-white tracking-tight" data-testid="supervisor-heading">Build Orders</h1>
            <p className="mt-1.5 text-white/50 text-sm max-w-2xl">Create an order, generate a secure customer viewing link, hand off to Station.</p>
          </div>
        </div>
        <SummaryCards />
        <div className="rounded-xl border overflow-hidden" style={{ borderColor: "rgba(255,255,255,0.06)", background: "#0a0a0d" }} data-testid="build-orders-content">
          <AdminDashboard embedded />
        </div>
      </div>
    </SupervisorLayout>
  );
}
